import { useEffect, useRef, useState } from 'react'
import type {
  LoadEffectiveTimeBlocks,
  SchedulableLocationType,
  TimeBlock,
} from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'

export type EffectiveTimeBlocksLocation = {
  locationType: SchedulableLocationType
  locationId: string
}

export type EffectiveTimeBlocksOrgContext = {
  orgId: string
  orgType: 'club' | 'outfitter'
}

export function useEffectiveTimeBlocks(
  loadEffectiveTimeBlocks: LoadEffectiveTimeBlocks,
  orgContext: EffectiveTimeBlocksOrgContext | null,
  location?: EffectiveTimeBlocksLocation | null,
  onResolved?: (timeBlocks: TimeBlock[]) => void,
): TimeBlock[] {
  const [timeBlocks, setTimeBlocks] = useState<TimeBlock[]>([])
  const loadRef = useRef(loadEffectiveTimeBlocks)
  loadRef.current = loadEffectiveTimeBlocks
  const onResolvedRef = useRef(onResolved)
  onResolvedRef.current = onResolved

  const orgId = orgContext?.orgId ?? null
  const orgType = orgContext?.orgType ?? null
  const locationType = location?.locationType ?? null
  const locationId = location?.locationId ?? null

  useEffect(() => {
    if (!orgId || !orgType) {
      setTimeBlocks([])
      return
    }
    let active = true
    loadRef
      .current({
        orgId,
        orgType,
        ...(locationType && locationId ? { locationType, locationId } : {}),
      })
      .then((result) => {
        if (!active) return
        setTimeBlocks(result.timeBlocks)
        onResolvedRef.current?.(result.timeBlocks)
      })
      .catch((error: unknown) => {
        if (active) setTimeBlocks([])
        reportError(error, { fallbackMessage: 'Failed to load time blocks.' })
      })
    return () => {
      active = false
    }
  }, [orgId, orgType, locationType, locationId])

  return timeBlocks
}
